<script setup lang="ts">
import { ClubAccountHistory } from '@/Pejer/types/ClubAccount'
import { ArrowDownLeft, ArrowUpRight } from 'lucide-vue-next'
import { computed } from 'vue'
import { formatAccount, formatKroner } from './types'

const props = defineProps<{
  history: ClubAccountHistory | null | undefined
  isLoading: boolean
}>()

const refundReasons: Record<string, string> = {
  PaymentRefund: 'Tilbakebetaling',
  TransferExcessPayment: 'Overbetaling',
  PaymentIsCancelled: 'Betalt etter kansellering',
  KIDNotFound: 'Ukjent KID',
}

const rows = computed(() => {
  const payments = (props.history?.payments ?? []).map((p) => ({
    key: `p-${p.date}-${p.amount}-${p.fromBankAccount}`,
    date: p.date,
    kind: 'payment' as const,
    title: p.isManual ? 'Innbetaling (registrert manuelt)' : 'Innbetaling',
    account: p.fromBankAccount ? `fra ${formatAccount(p.fromBankAccount)}` : null,
    detail: p.note,
    amount: p.amount,
    pending: false,
  }))
  const refunds = (props.history?.refunds ?? []).map((r) => ({
    key: `r-${r.date}-${r.amount}-${r.toBankAccount}`,
    date: r.date,
    kind: 'refund' as const,
    title:
      (r.reason && refundReasons[r.reason]) ||
      'Tilbakebetaling',
    account: r.toBankAccount ? `til ${formatAccount(r.toBankAccount)}` : null,
    detail: r.isCompleted
      ? r.isManual
        ? 'Betalt fra klubbens nettbank'
        : null
      : 'Venter: klubben må betale fra nettbanken og markere som utført',
    amount: -r.amount,
    pending: !r.isCompleted,
  }))
  return [...payments, ...refunds].sort(
    (a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime()
  )
})

const formatDate = (date: string | null) =>
  date ? new Date(date).toLocaleDateString('no-NO', { day: 'numeric', month: 'short', year: 'numeric' }) : ''
</script>
<template>
  <div class="flex flex-col gap-1">
    <span class="font-medium">Historikk</span>
    <span v-if="isLoading" class="text-slate-500">Henter historikk …</span>
    <span v-else-if="rows.length === 0" class="text-slate-500">Ingen innbetalinger ennå.</span>
    <ul v-else class="flex flex-col divide-y rounded-md border">
      <li v-for="row in rows" :key="row.key" class="flex items-start gap-2 px-3 py-2">
        <ArrowDownLeft v-if="row.kind === 'payment'" :size="16" class="shrink-0 mt-0.5 text-green-600" />
        <ArrowUpRight v-else :size="16" class="shrink-0 mt-0.5 text-red-600" />
        <div class="flex flex-col flex-1 min-w-0">
          <span>
            {{ row.title }}
            <span v-if="row.account" class="font-mono text-slate-600">{{ row.account }}</span>
          </span>
          <span v-if="row.detail" class="text-xs" :class="row.pending ? 'text-amber-700' : 'text-slate-500'">
            {{ row.detail }}
          </span>
        </div>
        <div class="flex flex-col items-end shrink-0">
          <span :class="row.amount < 0 ? 'text-red-600' : 'text-green-700'">
            {{ row.amount < 0 ? '−' : '+' }}{{ formatKroner(Math.abs(row.amount) / 100) }}
          </span>
          <span class="text-xs text-slate-500">{{ formatDate(row.date) }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>
